import React, { useContext } from 'react';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import PropTypes from 'prop-types';
import Input from '../../elements/Input';
import GuestsSelectorPanel from '../GuestsSelector/GuestsSelectorPanel';
import { getInputText } from '../GuestsSelector/commons';
import Box from '../../elements/Box';
import Button from '../../elements/Button';

function GuestsSelectorMobile({
  showClear,
  dispatch,
  onApply,
  onClear,
  value,
  focusReferences,
  showDetails,
  applyFilter,
  details,
  ...props
}) {
  const { adults, children, infants } = value;
  const { translate, direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  const guestsCount = adults + children;
  const totalCount = guestsCount + infants;
  const inputText = getInputText(translate, value);

  const reset = () => {
    dispatch({ type: 'CLEAR' });
    onClear();
  };

  function handleChange() {
    onApply(value);
  }

  if (showDetails) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="between">
        <GuestsSelectorPanel value={value} dispatch={dispatch} {...details} />
        <Button {...applyFilter} onClick={handleChange}>
          {translate('cmp-guests-select-btn-apply', 'Apply')}
        </Button>
      </Box>
    );
  }

  return (
    <Input
      type="text"
      {...props}
      value={guestsCount > 0 ? inputText : ''}
      dataTestId="guests-filter-input"
      className={`fr-guests-filter ${className}`}
      style={style}
      rightIcons={showClear && totalCount > 0 ? ['close'] : []}
      onRightIconClick={reset}
      leftIcons={['avatar']}
      leftTabIndex={[-1]}
      leftAriaLabel={[translate('cmp-guests-aria-label-icon-coins', 'Coins')]}
      placeholder={translate('cmp-guests-placeholder', 'Any Guests')}
      label={translate('cmp-guests-label', 'Guests')}
      ariaLabelDescription={translate(
        'cmp-guests-label-description',
        'Select the number of guests'
      )}
      ariaLabelInvalidDescription={translate(
        'cmp-guests-label-invalid-description',
        'Invalid format, please change the value'
      )}
      focusReferences={focusReferences}
    />
  );
}

GuestsSelectorMobile.propTypes = {
  /**
   *  CSS Class to apply to input.
   */
  className: PropTypes.string,
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * Show clear button
   */
  showClear: PropTypes.bool,
  /**
   * Value of the budget
   */
  value: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
    infants: PropTypes.number,
  }),
  /**
   * LeftIconsProps by default
   */
  leftIconsProps: PropTypes.objectOf(PropTypes.any),
  /**
   * Function for dispatching actions
   * ({ type: 'INCREASE' | 'DECREASE', field: 'adults' | 'children' | 'adults' } | { type: 'CLEAR' })
   */
  dispatch: PropTypes.func,
  /**
   * onApply function
   */
  onApply: PropTypes.func,
  /**
   * onClear input function
   */
  onClear: PropTypes.func,
  /**
   * Forwarded ref
   */
  focusReferences: PropTypes.func,
  /**
   * Wether to show the detailed or simplified view
   */
  showDetails: PropTypes.bool,
  /**
   * Apply filter button
   */
  applyFilter: PropTypes.objectOf(PropTypes.any),
  /**
   * Css style applied to the component
   */
  details: PropTypes.objectOf(PropTypes.any),
};

GuestsSelectorMobile.defaultProps = {
  className: '',
  style: {},
  showClear: true,
  value: {
    adults: 0,
    children: 0,
    infants: 0,
  },
  leftIconsProps: {
    color: 'transparent',
  },
  dispatch: () => {},
  onApply: () => {},
  onClear: () => {},
  focusReferences: () => {},
  showDetails: false,
  applyFilter: {
    color: 'dark',
    width: '100',
    marginBottom: '2',
    label: {
      marginLeft: '2',
    },
  },
  details: {
    style: {
      minHeight: '500px',
    },
  },
};

GuestsSelectorMobile.componentName = 'GuestsSelectorMobile';

export default GuestsSelectorMobile;
